// "Office notices" dashboard block (Session ATZ, email system Phase 2, Item 1).
//
// WHY IT EXISTS. The platform used to email its own inbox to tell the office things: a quote accepted, a contract signed,
// a draft invoice raised or not raised, a timesheet submitted, a repair estimate answered. Those emails buried real
// customer mail in info@. They now arrive HERE instead, written by the one writer (server officeNotices.ts), unread first.
// The safety alerts (an entrapment, an SLA breach, a missed lone worker check-in) are NOT here: they still go by email and
// text, exactly as before.
//
// Self-fetching with its own loading and error states, like every registered block. Marking read and deleting are the
// office's own housekeeping; a delete is a two-step confirm (the platform's delete rule).
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import type { OfficeNotice } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { ConfirmButton } from "../../components/ConfirmButton";
import { EmptyState } from "../../components/EmptyState";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { StatusPill } from "../../components/StatusPill";
import { formatDateTime } from "../../lib/format";

const SHOWN = 8;

/** "2 Oct 2026, 09:15" in London time (the platform's one date-and-time format). Pure. */
export function noticeWhen(iso: string): string {
  return formatDateTime(iso);
}

export function OfficeNoticesPanel(): ReactNode {
  const [notices, setNotices] = useState<OfficeNotice[]>([]);
  const [unread, setUnread] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(false);
    api.officeNotices
      .list({ limit: SHOWN })
      .then((r) => {
        if (!live) return;
        setNotices(r.notices);
        setUnread(r.unreadCount);
        setTotal(r.total);
      })
      .catch(() => live && setError(true))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [reloadKey]);

  async function act(key: string, fn: () => Promise<unknown>) {
    setBusy(key);
    setActionError(null);
    try {
      await fn();
      setReloadKey((k) => k + 1);
    } catch (err) {
      setActionError(friendlyApiError(err, "That could not be done. Please try again."));
    } finally {
      setBusy(null);
    }
  }

  const readCount = notices.filter((n) => n.read).length;

  return (
    <section className="mb-6 rounded-ele border border-ele-purple-accent bg-ele-surface p-4" aria-label="Office notices">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="ele-section-title">
          Office notices{unread > 0 && <span className="ml-2 rounded-full bg-ele-green px-2 py-0.5 text-xs text-ele-bg">{unread} new</span>}
        </h2>
        <div className="flex flex-wrap gap-3">
          {unread > 0 && (
            <button
              type="button"
              onClick={() => void act("all", () => api.officeNotices.markAllRead())}
              disabled={busy != null}
              className="font-menu text-xs tracking-wide text-ele-blue hover:text-ele-strong"
            >
              {busy === "all" ? "Marking..." : "Mark all read"}
            </button>
          )}
          {readCount > 0 && (
            <ConfirmButton
              onConfirm={() => void act("clear", () => api.officeNotices.removeRead())}
              label="Clear read notices"
              confirmLabel="Delete every read notice? Press again"
              busy={busy === "clear"}
              busyLabel="Clearing..."
              disabled={busy != null}
              className="font-menu text-xs tracking-wide text-ele-blue hover:text-ele-strong"
            />
          )}
        </div>
      </div>

      {actionError && (
        <p role="alert" className="mb-2 text-sm text-ele-danger">
          {actionError}
        </p>
      )}

      {loading ? (
        <Loader label="Loading the office notices..." rows={3} />
      ) : error ? (
        <div role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4 text-center">
          <p className="text-ele-strong">Could not load the office notices.</p>
          <Button variant="secondary" size="sm" className="mt-3" onClick={() => setReloadKey((k) => k + 1)}>
            Retry
          </Button>
        </div>
      ) : notices.length === 0 ? (
        <EmptyState compact message="Nothing to report. Notices the platform used to email to the office appear here." />
      ) : (
        <ul className="space-y-2">
          {notices.map((n) => {
            const open = expanded.has(n.id);
            return (
              <li key={n.id} className={`rounded-ele border px-3 py-2 ${n.read ? "border-ele-hairline" : "border-ele-purple-accent bg-ele-surface-soft"}`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  {/* min-w-[9rem], not min-w-0 (Session BDY): with no minimum the buttons beside the title never wrapped below it, and a "Needs action" pill in a 60px column spilled at a larger text size. */}
                  <div className="min-w-[9rem] flex-1">
                    <p className={`text-sm [overflow-wrap:anywhere] ${n.read ? "text-ele-body" : "font-semibold text-ele-strong"}`}>
                      {n.severity === "action" && <StatusPill word="Needs action" tone="amber" className="mr-2" />}
                      {n.title}
                    </p>
                    <p className="text-xs text-ele-body">
                      {noticeWhen(n.raisedAt)}
                      {n.raiseCount > 1 ? `, raised ${n.raiseCount} times` : ""}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    {n.linkPath && (
                      <Link
                        to={n.linkPath}
                        onClick={() => !n.read && void api.officeNotices.markRead(n.id).catch(() => undefined)}
                        className="font-menu text-xs tracking-wide text-ele-green hover:text-ele-strong"
                      >
                        Open
                      </Link>
                    )}
                    {n.body && (
                      <button
                        type="button"
                        aria-expanded={open}
                        onClick={() => setExpanded((s) => (s.has(n.id) ? new Set([...s].filter((x) => x !== n.id)) : new Set([...s, n.id])))}
                        className="font-menu text-xs tracking-wide text-ele-blue hover:text-ele-strong"
                      >
                        {open ? "Hide" : "Details"}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => void act(n.id, () => (n.read ? api.officeNotices.markUnread(n.id) : api.officeNotices.markRead(n.id)))}
                      disabled={busy != null}
                      className="font-menu text-xs tracking-wide text-ele-blue hover:text-ele-strong"
                    >
                      {n.read ? "Mark unread" : "Mark read"}
                    </button>
                    <ConfirmButton
                      onConfirm={() => void act(`del:${n.id}`, () => api.officeNotices.remove(n.id))}
                      label="Delete"
                      confirmLabel="Delete? Press again"
                      busy={busy === `del:${n.id}`}
                      disabled={busy != null}
                      className="font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong"
                    />
                  </div>
                </div>
                {open && n.body && <p className="mt-2 whitespace-pre-line text-sm text-ele-body [overflow-wrap:anywhere]">{n.body}</p>}
              </li>
            );
          })}
        </ul>
      )}
      {!loading && !error && total > notices.length && (
        <p className="mt-2 text-xs text-ele-body">
          Showing the {notices.length} most recent of {total}. Mark some read and clear them to see the rest.
        </p>
      )}
    </section>
  );
}
